import React from 'react';
import { useLanguage } from '@/contexts/language/LanguageContext';
import { homeCopy } from '@/contexts/language/translations/pages/home';

/** 11TS: black letters on light bg – use inverted SVG mask so only letters are #221A40 */
const LOGO_11TS = '/lovable-uploads/11TS_logo.png';
const LOGO_BLUE = '#221A40';

/** Logo order, left to right in the strip */
const LOGOS_IN_ORDER = [
  LOGO_11TS,
  '/lovable-uploads/blendea_logo.png',
  '/lovable-uploads/donoci_logo.png',
  '/lovable-uploads/alpinresorts_logo.png',
  '/lovable-uploads/econea_logo.png',
  '/lovable-uploads/herohero_logo.png',
  '/lovable-uploads/svet-sviditel_logo.png',
  '/lovable-uploads/envipak_logo.png',
  '/lovable-uploads/t4r_logo.png',
  '/lovable-uploads/mindbank_logo.png',
  '/lovable-uploads/ecoprodukt_logo.svg',
  '/lovable-uploads/stahlmann_logo.svg',
  '/lovable-uploads/juwelier_logo.svg',
];

/** Per-logo width override — the near-square ecoprodukt mark reads bigger than the wordmarks at full width */
const LOGO_MAX_WIDTH: Record<string, string> = {
  '/lovable-uploads/ecoprodukt_logo.svg': 'max-w-[110px]',
};

/** One logo. idSuffix keeps the 11TS mask ids unique per instance */
const renderLogo = (logo: string, label: string, idSuffix: string, maxWidth: string) =>
  logo === LOGO_11TS ? (
    <svg
      className={`w-full ${maxWidth} h-16 md:h-20 shrink-0`}
      viewBox="0 0 140 56"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={label}
    >
      <defs>
        <filter id={`logo-11ts-invert-${idSuffix}`} colorInterpolationFilters="sRGB">
          <feColorMatrix type="matrix" values="-1 0 0 0 1  0 -1 0 0 1  0 0 -1 0 1  0 0 0 1 0" />
        </filter>
        <mask id={`logo-11ts-mask-${idSuffix}`}>
          <image
            href={LOGO_11TS}
            x="0"
            y="0"
            width="140"
            height="56"
            preserveAspectRatio="xMidYMid meet"
            filter={`url(#logo-11ts-invert-${idSuffix})`}
          />
        </mask>
      </defs>
      <rect x="0" y="0" width="100%" height="100%" fill={LOGO_BLUE} mask={`url(#logo-11ts-mask-${idSuffix})`} />
    </svg>
  ) : (
    <div
      className={`w-full ${LOGO_MAX_WIDTH[logo] ?? maxWidth} h-16 md:h-20 bg-[#221A40] shrink-0`}
      style={{
        maskImage: `url(${logo})`,
        maskSize: 'contain',
        maskRepeat: 'no-repeat',
        maskPosition: 'center',
        WebkitMaskImage: `url(${logo})`,
        WebkitMaskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
      }}
      role="img"
      aria-label={label}
    />
  );

const ClientLogosSection = () => {
  const { t, language } = useLanguage();
  const c = homeCopy[language].logos;

  return (
    <section className="pt-0 pb-12 bg-white relative overflow-hidden">
      <div className="container-custom relative z-10">
        <div className="text-center mb-3 pt-12 md:pt-16">
          <p className="text-base md:text-lg font-medium text-muted-foreground">
            {t('companiesSubtitle')}
          </p>
        </div>
      </div>

      {/* Wide, page-width grid: 7 + 6 logos on desktop; the incomplete last row is centred */}
      <div className="flex flex-wrap justify-center items-center w-full max-w-[1600px] mx-auto px-4 md:px-8 relative z-10">
        {LOGOS_IN_ORDER.map((logo, index) => (
          <div
            key={logo}
            className="flex items-center justify-center h-[96px] md:h-[112px] w-1/2 sm:w-1/3 md:w-1/4 lg:w-1/5 xl:w-[14.2857%] px-3 md:px-4"
          >
            {renderLogo(logo, `${c.clientLogo} ${index + 1}`, `${index}`, 'max-w-[170px]')}
          </div>
        ))}
      </div>
    </section>
  );
};

export default ClientLogosSection;
